/**
 * The Crew section of a well job (api#1057): who was on the well, in which
 * role, from when to when. One row per spell; a hand rotating off and back on
 * is two rows. `off_at` empty means still on.
 *
 * TIMES ARE THE TENANT'S WALL CLOCK, as on the job's own steps: the picker
 * starts from the instant in the company's zone and an offset-free value is
 * sent as typed, which the API reads in that zone.
 *
 * WRITES are the job's (DOWNHOLE_JOB_WRITE_ROLES). On a CLOSED job the API
 * freezes the crew except for notes (JOB_CLOSED), so Add and Remove are not
 * offered there and Edit opens with only the notes enabled.
 *
 * The person picker lists the company's users, minus anyone terminated (the
 * API refuses them with CREW_MEMBER_INACTIVE). Refusals, CREW_OVERLAP above
 * all, are shown as the API wrote them: its sentence names the spell in the way.
 *
 * The rows hold Edit and Remove buttons, so they are NOT row targets (the
 * Rate Sheets rule).
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, getApiErrorMessage } from '../../api/client';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useAddCrew, useRemoveCrew, useUpdateCrew } from '../../hooks/useDownholeJobs';
import { unwrapArray } from '../../lib/api-envelope';
import { formatInstant } from '../../lib/calendar-date';
import { isJobClosed } from '../../lib/downhole-jobs';
import { DH_CREW_SHIFT_LABEL, DH_PERSONNEL_ROLE_LABEL, dhLabel } from '../../lib/downhole-labels';
import { onlineOrSay } from '../../lib/downhole-offline';
import { LOAD_FAILURE_QUERY_OPTIONS, queryLoadFailure } from '../../lib/load-failure';
import { DH_CREW_SHIFTS, DH_PERSONNEL_ROLES, type DhCrewSpell, type DhCrewWrite, type DhJobDetail } from '../../types/downhole';
import { DH_DATE_INPUT, DH_INPUT, DH_SECONDARY, DhDialog, DhEmpty, DhField, DhPanel, useOpenCount } from './DownholeBits';
import { tenantLocalInput } from './RunDialogs';

const PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-60';
/** A spell time carries its year, like the job's stamps. */
const SPELL_FORMAT: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };

interface CrewPerson {
  id: string;
  fullName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  employmentStatus?: string | null;
}

const personName = (u: CrewPerson) => u.fullName || [u.firstName, u.lastName].filter(Boolean).join(' ') || '—';

function usePeople(enabled: boolean) {
  return useQuery({
    queryKey: ['users'],
    queryFn: async () => unwrapArray<CrewPerson>((await api.users.list()).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function CrewPanel({ job, tz, canWrite }: { job: DhJobDetail; tz: string | null; canWrite: boolean }) {
  const [editing, setEditing] = useState<DhCrewSpell | 'new' | null>(null);
  const [removing, setRemoving] = useState<DhCrewSpell | null>(null);
  const closed = isJobClosed(job.status);
  const crew = job.crew;
  const at = (v: string | null) => (v ? formatInstant(v, tz, SPELL_FORMAT) ?? '—' : null);

  return (
    <DhPanel
      eyebrow="People"
      title="Crew"
      testId="well-crew"
      actions={
        canWrite && !closed && crew ? (
          <button type="button" className={DH_SECONDARY} onClick={() => setEditing('new')}>
            <Plus className="size-4" aria-hidden="true" />
            Add crew
          </button>
        ) : undefined
      }
    >
      {!crew ? (
        // An answer from before batch 3 carries no crew key: that is not "no crew".
        <DhEmpty>The crew list did not come with this job. Reload the page to see it.</DhEmpty>
      ) : crew.length === 0 ? (
        <DhEmpty>No one is on this job yet.</DhEmpty>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[880px] text-left">
            <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
              <tr>
                <th scope="col" className="px-4 py-3">Person</th>
                <th scope="col" className="px-4 py-3">Role</th>
                <th scope="col" className="px-4 py-3">Shift</th>
                <th scope="col" className="px-4 py-3">On the well</th>
                <th scope="col" className="px-4 py-3">Off the well</th>
                <th scope="col" className="px-4 py-3">Notes</th>
                {canWrite && <th scope="col" className="px-4 py-3 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {crew.map((w) => (
                <tr key={w.id} className="border-t border-fieldiq-border" data-testid={`crew-${w.id}`}>
                  <td className="px-4 py-3 text-sm font-bold text-white">{displayValue(w.person_name)}</td>
                  <td className="px-4 py-3 text-sm text-fieldiq-text">{dhLabel(DH_PERSONNEL_ROLE_LABEL, w.personnel_role)}</td>
                  <td className="px-4 py-3 text-sm text-fieldiq-text">{dhLabel(DH_CREW_SHIFT_LABEL, w.shift)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">{at(w.on_at) ?? '—'}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">
                    {w.off_at ? at(w.off_at) : <span className="font-bold">Still on</span>}
                  </td>
                  <td className="max-w-[18rem] truncate px-4 py-3 text-sm text-fieldiq-text">{displayValue(w.notes)}</td>
                  {canWrite && (
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <span className="inline-flex gap-2">
                        <button type="button" className={DH_SECONDARY} onClick={() => setEditing(w)} aria-label={`Edit ${w.person_name ?? 'crew member'}`}>
                          <Pencil className="size-4" aria-hidden="true" />
                          Edit
                        </button>
                        {!closed && (
                          <button type="button" className={DH_SECONDARY} onClick={() => setRemoving(w)} aria-label={`Remove ${w.person_name ?? 'crew member'}`}>
                            <Trash2 className="size-4" aria-hidden="true" />
                          </button>
                        )}
                      </span>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canWrite && editing && (
        <CrewDialog job={job} spell={editing === 'new' ? null : editing} tz={tz} onClose={() => setEditing(null)} />
      )}
      {canWrite && removing && <RemoveCrewDialog job={job} spell={removing} onClose={() => setRemoving(null)} />}
    </DhPanel>
  );
}

type Values = { user_id: string; personnel_role: string; shift: string; on_at: string; off_at: string; notes: string };

function valuesOf(spell: DhCrewSpell | null, tz: string | null): Values {
  return {
    user_id: spell?.user_id ?? '',
    personnel_role: spell?.personnel_role ?? '',
    shift: spell?.shift ?? '',
    on_at: spell ? tenantLocalInput(spell.on_at, tz) : '',
    off_at: spell?.off_at ? tenantLocalInput(spell.off_at, tz) : '',
    notes: spell?.notes ?? '',
  };
}

/**
 * The body to send. A create sends what was filled (user, role and on are
 * required); an edit sends only what moved, a cleared optional field as null.
 * `user_id` is never sent on an edit: the API answers COMPUTED_FIELD.
 */
export function crewWriteBody(v: Values, spell: DhCrewSpell | null, tz: string | null): DhCrewWrite {
  const before = valuesOf(spell, tz);
  const body: Record<string, string | null> = {};
  (['user_id', 'personnel_role', 'shift', 'on_at', 'off_at', 'notes'] as const).forEach((k) => {
    if (spell && k === 'user_id') return;
    const value = v[k].trim();
    if (spell) {
      if (value === before[k].trim()) return;
      body[k] = value === '' ? null : value;
    } else if (value !== '') {
      body[k] = value;
    }
  });
  return body as DhCrewWrite;
}

function CrewDialog({ job, spell, tz, onClose }: { job: DhJobDetail; spell: DhCrewSpell | null; tz: string | null; onClose: () => void }) {
  const add = useAddCrew();
  const update = useUpdateCrew();
  const people = usePeople(true);
  const openCount = useOpenCount(true);
  const [v, setV] = useState<Values>(() => valuesOf(spell, tz));
  const [error, setError] = useState<string | null>(null);
  React.useEffect(() => {
    setV(valuesOf(spell, tz));
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openCount]);

  const closed = isJobClosed(job.status);
  const pending = add.isPending || update.isPending;
  const set = (k: keyof Values) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setV((s) => ({ ...s, [k]: e.target.value }));
  // Terminated people are refused by the API (CREW_MEMBER_INACTIVE); they are left off the list.
  const offered = (people.data ?? []).filter((u) => u.employmentStatus !== 'terminated');

  const submit = () => {
    setError(null);
    if (!spell && !v.user_id) return setError('Choose the person.');
    if (!v.personnel_role) return setError('Choose the role.');
    if (!v.on_at) return setError('Enter when they went on the well.');
    const body = crewWriteBody(v, spell, tz);
    if (spell && Object.keys(body).length === 0) return onClose();
    if (!onlineOrSay(setError)) return;
    const done = {
      onSuccess: () => {
        toast.success(spell ? 'Crew spell saved' : 'Added to the crew');
        onClose();
      },
      onError: (err: unknown) => setError(getApiErrorMessage(err, 'The crew could not be saved.')),
    };
    if (spell) update.mutate({ jobId: job.id, crewId: spell.id, body }, done);
    else add.mutate({ jobId: job.id, body }, done);
  };

  const locked = closed || pending;
  return (
    <DhDialog
      open
      title={spell ? `Edit ${spell.person_name ?? 'crew spell'}` : `Add crew to ${job.job_number}`}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose} disabled={pending}>Cancel</button>
          <button type="button" className={PRIMARY} onClick={submit} disabled={pending}>
            {pending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            {spell ? 'Save' : 'Add to crew'}
          </button>
        </>
      }
    >
      {closed && <p className="mb-3 text-sm text-fieldiq-muted">This job is closed. Only crew notes can be changed.</p>}
      <div className="grid gap-3 sm:grid-cols-2">
        <DhField label="Person *">
          {spell ? (
            <input className={DH_INPUT} value={spell.person_name ?? '—'} disabled aria-label="Person" />
          ) : (
            <>
              <select className={DH_INPUT} value={v.user_id} onChange={set('user_id')} disabled={locked} aria-label="Person">
                <option value="">Choose a person</option>
                {offered.map((u) => <option key={u.id} value={u.id}>{personName(u)}</option>)}
              </select>
              {queryLoadFailure(people) && <span className="mt-1 block text-xs text-fieldiq-muted">People could not be loaded.</span>}
            </>
          )}
        </DhField>
        <DhField label="Role *">
          <select className={DH_INPUT} value={v.personnel_role} onChange={set('personnel_role')} disabled={locked} aria-label="Role">
            <option value="">Choose a role</option>
            {DH_PERSONNEL_ROLES.map((r) => <option key={r} value={r}>{DH_PERSONNEL_ROLE_LABEL[r]}</option>)}
          </select>
        </DhField>
        <DhField label="Shift">
          <select className={DH_INPUT} value={v.shift} onChange={set('shift')} disabled={locked} aria-label="Shift">
            <option value="">Not recorded</option>
            {DH_CREW_SHIFTS.map((s) => <option key={s} value={s}>{DH_CREW_SHIFT_LABEL[s]}</option>)}
          </select>
        </DhField>
        <div />
        <DhField label="On the well *" hint="On the company's clock.">
          <input type="datetime-local" className={DH_DATE_INPUT} value={v.on_at} onChange={set('on_at')} disabled={locked} aria-label="On the well" />
        </DhField>
        <DhField label="Off the well" hint="Leave blank while they are still on.">
          <input type="datetime-local" className={DH_DATE_INPUT} value={v.off_at} onChange={set('off_at')} disabled={locked} aria-label="Off the well" />
        </DhField>
      </div>
      <div className="mt-3">
        <DhField label="Notes">
          <textarea className={`${DH_INPUT} h-20 py-2`} value={v.notes} maxLength={10000} onChange={set('notes')} disabled={pending} aria-label="Notes" />
        </DhField>
      </div>
      {error && (
        <p role="alert" className="mt-3 rounded-fi-control border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
          {error}
        </p>
      )}
    </DhDialog>
  );
}

function RemoveCrewDialog({ job, spell, onClose }: { job: DhJobDetail; spell: DhCrewSpell; onClose: () => void }) {
  const remove = useRemoveCrew();
  const [error, setError] = useState<string | null>(null);
  const submit = () => {
    setError(null);
    if (!onlineOrSay(setError)) return;
    remove.mutate(
      { jobId: job.id, crewId: spell.id },
      {
        onSuccess: () => {
          toast.success('Removed from the crew');
          onClose();
        },
        onError: (err) => setError(getApiErrorMessage(err, 'The crew spell could not be removed.')),
      },
    );
  };
  return (
    <DhDialog
      open
      title={`Remove ${spell.person_name ?? 'this crew spell'}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose} disabled={remove.isPending}>Keep</button>
          <button type="button" className={PRIMARY} onClick={submit} disabled={remove.isPending}>
            {remove.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Remove from crew
          </button>
        </>
      }
    >
      <p className="text-sm text-fieldiq-text">
        This removes {spell.person_name ?? 'this person'}'s spell as {dhLabel(DH_PERSONNEL_ROLE_LABEL, spell.personnel_role).toLowerCase()} from {job.job_number}.
      </p>
      {error && (
        <p role="alert" className="mt-3 rounded-fi-control border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
          {error}
        </p>
      )}
    </DhDialog>
  );
}
